import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { ContextTooltip } from '@/components/ui/context-tooltip';
import { DatePicker } from '@/components/ui/date-picker';
import { DisabledFieldset } from '@/components/ui/disabled-fieldset';
import { FieldError } from '@/components/ui/field-error';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useInstruments } from '@/hooks/queries/markets/use-markets';
import { useQuoteSources } from '@/hooks/queries/mm-programs/use-mm-programs';
import { HTTP_CONFLICT, isHttpStatus } from '@/lib/api/error';
import { toDisplayState } from '@/services/api/markets';
import { type SaveMmProgramResponse, updateMmProgram } from '@/services/api/mm-programs';
import { formatUsd } from '@/utils/format';
import { isFiniteNumber } from '@/utils/number';
import { pluralize } from '@/utils/string';
import { nextScheduleStartBounds } from './date-bounds';
import { DowntimeSection } from './downtime-section';
import { FIELD_DESCRIPTIONS } from './field-descriptions';
import {
  appendNextSchedule,
  blankProgram,
  compensationOf,
  dayLabel,
  feedsByInstrument,
  INSTRUMENT_PATTERN,
  isOpen,
  mergeSessions,
  orderBySession,
  PROGRAM_TYPE_LABELS,
  sessionGroupIndices,
  splitIntoSessions,
  toDraft,
  todayIsoDate,
  toIsoDate,
} from './mm-program-editing';
import { TextField, TextListField } from './mm-program-fields';
import { findSettingsProblems, isPerpetual, type MarketMaker, type MarketMakerInput, marketMakerSchema, type ProgramInput } from './mm-program-schema';
import { ProgramGroupForm } from './program-group-form';
import { ScheduleTimeline } from './schedule-timeline';
import { describeSettingsChanges } from './settings-changes';
import { SettingsChangesList } from './settings-changes-list';

const DIRTY = { shouldDirty: true } as const;
const CONFLICT_MESSAGE = 'Someone saved this market maker after you opened it. Cancel, then reload to see their changes before editing again.';

/** Whole dollars, or a dash while a field being edited is blank or not yet a number. */
const formatAmount = (value: number | null | undefined) => (isFiniteNumber(value) ? formatUsd(value, 0) : '—');

const bucketSummary = (bucket: ProgramInput['b1']) => `${bucket.bps} bps · ${formatAmount(bucket.kpi)}`;

const firstOpenIndex = (programs: ProgramInput[]) => Math.max(programs.findIndex(isOpen), 0);

interface MarketMakerEditorProps {
  marketMaker: MarketMakerInput;
  version: number;
  canEdit: boolean;
  onSaved: (response: SaveMmProgramResponse) => void;
  onReload: () => void;
}

/** Mounted per loaded version, so the version it opened with is the one the save is checked against. */
export const MarketMakerEditor = ({ marketMaker, version, canEdit, onSaved, onReload }: MarketMakerEditorProps) => {
  const exchangeMarkets = useInstruments();
  const marketTypes = useMemo(() => new Map((exchangeMarkets.data ?? []).map((market) => [market.id, market.type])), [exchangeMarkets.data]);
  const quoteSources = useQuoteSources();
  const initial = useMemo(() => toDraft(marketMaker), [marketMaker]);
  const form = useForm<MarketMakerInput, unknown, MarketMaker>({ resolver: zodResolver(marketMakerSchema), defaultValues: initial });
  const values = form.watch();
  const programs = values.programs ?? {};
  const addableMarkets = (exchangeMarkets.data ?? [])
    .filter((market) => !programs[market.id])
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((market) => ({ value: market.id, label: market.id, detail: toDisplayState(market.state) }));
  const instruments = Object.keys(programs).sort();

  const [selectedInstrument, setSelectedInstrument] = useState(instruments[0] ?? '');
  const [selectedIndex, setSelectedIndex] = useState(() => firstOpenIndex(programs[instruments[0]] ?? []));
  const [newInstrument, setNewInstrument] = useState('');
  const [newStart, setNewStart] = useState(() => todayIsoDate().slice(0, 10));
  const [actionError, setActionError] = useState('');
  const [hasConflict, setHasConflict] = useState(false);
  const newInstrumentId = useId();
  const newStartId = useId();
  const activeId = useId();

  const problems = findSettingsProblems(values);
  const problemsByPath = new Map<string, string>();
  for (const problem of problems) if (!problemsByPath.has(problem.path)) problemsByPath.set(problem.path, problem.message);

  const schedules = programs[selectedInstrument] ?? [];
  const groupIndices = orderBySession(sessionGroupIndices(schedules, selectedIndex), schedules);
  const group = groupIndices.map((index) => schedules[index]);
  // Schedules are only ever appended, so a position still names the same schedule as loaded.
  const savedSchedules = initial.programs?.[selectedInstrument] ?? [];
  const savedGroup = groupIndices.every((index) => savedSchedules[index]) ? groupIndices.map((index) => savedSchedules[index]) : undefined;

  const isUnder = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}[`);
  const hasProblemOn = (instrument: string) => problems.some(({ path }) => isUnder(path, `programs.${instrument}`) || isUnder(path, `downtime.${instrument}`));
  const setPrograms = (next: Record<string, ProgramInput[]>) => form.setValue('programs', next, DIRTY);

  const selectInstrument = (instrument: string, list = programs[instrument] ?? []) => {
    setSelectedInstrument(instrument);
    setSelectedIndex(firstOpenIndex(list));
    setActionError('');
  };

  const addInstrument = () => {
    const code = newInstrument.trim().toUpperCase();
    if (!INSTRUMENT_PATTERN.test(code)) return setActionError('Use an instrument code such as BTC_USD_P.');
    if (programs[code]) return setActionError(`${code} is already on this market maker.`);
    // bop-api refuses new instruments the exchange does not list; when the list could not load it is the check.
    if (marketTypes.size && !marketTypes.has(code)) return setActionError(`${code} is not an exchange market.`);
    // Equity futures trade around US market hours, so they start with a bucket set per session.
    const start = [blankProgram(todayIsoDate(), code, feedsByInstrument(quoteSources.data?.sources ?? []).get(code))];
    const list = marketTypes.get(code) === 'EQUITY_FUTURE' ? splitIntoSessions(start, 0) : start;
    setPrograms({ ...programs, [code]: list });
    setNewInstrument('');
    selectInstrument(code, list);
  };

  const startNextSchedule = () => {
    const result = appendNextSchedule(schedules, toIsoDate(newStart));
    if ('error' in result) return setActionError(result.error);
    setPrograms({ ...programs, [selectedInstrument]: result.programs });
    setSelectedIndex(result.index);
    setActionError('');
  };

  const setTradingHours = (sessioned: boolean) => {
    const result = sessioned ? { programs: splitIntoSessions(schedules, groupIndices[0]), index: groupIndices[0] } : mergeSessions(schedules, groupIndices);
    setPrograms({ ...programs, [selectedInstrument]: result.programs });
    setSelectedIndex(result.index);
  };
  const offersTradingHours = marketTypes.get(selectedInstrument) === 'EQUITY_FUTURE' || group[0]?.session !== undefined;

  const updateGroup = (next: ProgramInput[]) => {
    const list = [...schedules];
    groupIndices.forEach((index, position) => {
      list[index] = next[position];
    });
    setPrograms({ ...programs, [selectedInstrument]: list });
  };

  const discard = () => {
    form.reset(initial);
    const loaded = initial.programs ?? {};
    const instrument = loaded[selectedInstrument] ? selectedInstrument : (Object.keys(loaded).sort()[0] ?? '');
    selectInstrument(instrument, loaded[instrument]);
  };

  const save = form.handleSubmit(
    async (prepared) => {
      const changes = describeSettingsChanges(marketMaker, prepared);
      if (!changes.length) {
        toast.info('Nothing to save: no field differs from the loaded settings.');
        return;
      }
      await confirmAction({
        title: `Save settings for ${prepared.name}`,
        description: <SettingsChangesList groups={changes} />,
        size: 'wide',
        actionLabel: 'Save',
        onConfirm: async (comment) => {
          try {
            const response = await updateMmProgram(prepared.p, { comment: comment.trim(), expectedVersion: version, marketMaker: prepared });
            toast.success(`Saved settings for ${response.marketMaker.name}`);
            onSaved(response);
          } catch (error) {
            if (isHttpStatus(error, HTTP_CONFLICT)) {
              setHasConflict(true);
              throw new Error(CONFLICT_MESSAGE);
            }
            throw error;
          }
        },
      });
    },
    () => toast.error('Fix the problems listed before saving.'),
  );

  return (
    <div className="flex flex-col gap-6">
      {hasConflict ? (
        <Alert variant="destructive">
          <AlertTitle>These settings changed since you opened them</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>Reloading discards your unsaved edits.</span>
            <Button onClick={onReload} size="sm" type="button" variant="outline">
              Reload
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Market maker</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <DisabledFieldset disabled={!canEdit}>
            <div className="md:col-span-2">
              <TextField
                description={FIELD_DESCRIPTIONS.name}
                error={problemsByPath.get('name')}
                label="Name"
                onChange={(name) => form.setValue('name', name, DIRTY)}
                value={values.name}
              />
            </div>
            <div className="md:col-span-2">
              <TextField
                description={FIELD_DESCRIPTIONS.email}
                error={problemsByPath.get('email')}
                label="Email"
                onChange={(email) => form.setValue('email', email, DIRTY)}
                value={values.email}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={values.active === true} id={activeId} onCheckedChange={(active) => form.setValue('active', active, DIRTY)} />
              <ContextTooltip description={FIELD_DESCRIPTIONS.active} htmlFor={activeId}>
                Active
              </ContextTooltip>
            </div>
            <div className="md:col-span-5">
              <TextListField
                description={FIELD_DESCRIPTIONS.bcc_emails}
                error={problems.find(({ path }) => path.startsWith('bcc_emails'))?.message}
                label="BCC"
                onChange={(emails) => form.setValue('bcc_emails', emails, DIRTY)}
                value={values.bcc_emails ?? []}
              />
            </div>
          </DisabledFieldset>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Programs</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-wrap items-end gap-2">
            {instruments.map((instrument) => (
              <Button
                className={cn(hasProblemOn(instrument) && 'border-red-500')}
                key={instrument}
                onClick={() => selectInstrument(instrument)}
                size="sm"
                type="button"
                variant={instrument === selectedInstrument ? 'primary' : 'outline'}
              >
                {instrument}
              </Button>
            ))}
            <DisabledFieldset disabled={!canEdit}>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col gap-1.5">
                  <ContextTooltip description={FIELD_DESCRIPTIONS.new_instrument} htmlFor={newInstrumentId}>
                    New instrument
                  </ContextTooltip>
                  {exchangeMarkets.isError ? (
                    <Input
                      className="w-56"
                      id={newInstrumentId}
                      onChange={(event) => setNewInstrument(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addInstrument();
                        }
                      }}
                      placeholder="BTC_USD_P"
                      value={newInstrument}
                    />
                  ) : (
                    <SearchableSelect
                      className="w-64"
                      disabled={exchangeMarkets.isPending}
                      empty="No markets found"
                      id={newInstrumentId}
                      onChange={setNewInstrument}
                      options={addableMarkets}
                      placeholder={exchangeMarkets.isPending ? 'Loading markets…' : 'Pick a market'}
                      searchPlaceholder="Search markets…"
                      value={newInstrument}
                    />
                  )}
                </div>
                <Button onClick={addInstrument} size="sm" type="button" variant="outline">
                  Add instrument
                </Button>
              </div>
            </DisabledFieldset>
          </div>

          {exchangeMarkets.isError ? (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              Exchange markets could not be loaded, so new instrument codes are not checked here. bop-api still checks them on save.
            </p>
          ) : null}
          <FieldError message={actionError} />

          {selectedInstrument && schedules.length ? (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h3 className="text-lg font-semibold">{selectedInstrument}</h3>
                <DisabledFieldset disabled={!canEdit}>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1.5">
                      <ContextTooltip description={FIELD_DESCRIPTIONS.new_schedule_start} htmlFor={newStartId}>
                        New schedule starts
                      </ContextTooltip>
                      <DatePicker className="w-44" id={newStartId} min={nextScheduleStartBounds(schedules).min} onChange={setNewStart} value={newStart} />
                    </div>
                    <Button onClick={startNextSchedule} type="button" variant="outline">
                      Start new schedule
                    </Button>
                  </div>
                </DisabledFieldset>
              </div>

              <ScheduleTimeline onSelect={setSelectedIndex} schedules={schedules} selected={groupIndices} />

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Variant</TableHead>
                    <TableHead>B1</TableHead>
                    <TableHead>B2</TableHead>
                    <TableHead>Compensation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map((program, index) => (
                    <TableRow
                      className={cn('cursor-pointer', groupIndices.includes(index) && 'bg-muted')}
                      // biome-ignore lint/suspicious/noArrayIndexKey: schedules have no id; position is their identity
                      key={index}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <TableCell>
                        {dayLabel(program.start_date)} → {dayLabel(program.end_date)}
                      </TableCell>
                      <TableCell>{program.session ?? 'All'}</TableCell>
                      <TableCell>{PROGRAM_TYPE_LABELS[program.type] ?? program.type}</TableCell>
                      <TableCell className="tabular-nums">{bucketSummary(program.b1)}</TableCell>
                      <TableCell className="tabular-nums">{bucketSummary(program.b2)}</TableCell>
                      <TableCell className="tabular-nums">{formatAmount(compensationOf(program))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {group.length ? (
                <DisabledFieldset disabled={!canEdit}>
                  <ProgramGroupForm
                    instrument={selectedInstrument}
                    key={`${selectedInstrument}:${groupIndices.join(',')}`}
                    onChange={updateGroup}
                    onTradingHoursChange={offersTradingHours ? setTradingHours : undefined}
                    problemAt={(position, field) => problemsByPath.get(`programs.${selectedInstrument}[${groupIndices[position]}].${field}`)}
                    programs={group}
                    saved={savedGroup}
                  />
                  {isPerpetual(selectedInstrument) ? (
                    <DowntimeSection
                      canEdit={canEdit}
                      instrument={selectedInstrument}
                      error={problems.find(({ path }) => isUnder(path, `downtime.${selectedInstrument}`))?.message}
                      onChange={(rules) => form.setValue(`downtime.${selectedInstrument}`, rules, DIRTY)}
                      pid={values.p}
                      rules={values.downtime?.[selectedInstrument] ?? []}
                    />
                  ) : null}
                </DisabledFieldset>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No programs yet. Add an instrument to start one.</p>
          )}
        </CardContent>
      </Card>

      {problems.length ? (
        <Alert variant="destructive">
          <AlertTitle>{pluralize(problems.length, 'problem')} to fix before saving</AlertTitle>
          <AlertDescription>
            <ul className="flex flex-col gap-1 text-sm">
              {problems.map((problem) => (
                <li key={`${problem.path}:${problem.message}`}>
                  <span className="font-mono text-xs">{problem.path || 'document'}</span>: {problem.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {canEdit ? (
        <div className="flex justify-end gap-2">
          <Button disabled={!form.formState.isDirty} onClick={discard} type="button" variant="outline">
            Discard changes
          </Button>
          <Button disabled={form.formState.isSubmitting} onClick={() => save()} type="button" variant="primary">
            Review and save
          </Button>
        </div>
      ) : null}
    </div>
  );
};
MarketMakerEditor.displayName = 'MarketMakerEditor';
